import { createContext, useCallback, useContext, useRef, useState } from 'react';
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter,
} from './ui/alert-dialog';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';

const ConfirmContext = createContext(null);

/**
 * In-app replacement for window.confirm / window.prompt (native popups are blocked in installed PWAs & webviews).
 *   const confirm = useConfirm();
 *   if (!(await confirm({ title: 'Delete?', destructive: true }))) return;          // boolean
 *   const reason = await confirm({ title: 'Reason?', withInput: true });             // string | null
 */
export function ConfirmProvider({ children }) {
  const [state, setState] = useState(null);
  const [value, setValue] = useState('');
  const resolver = useRef(null);

  const confirm = useCallback((opts) => new Promise((resolve) => {
    resolver.current = resolve;
    setValue(opts.defaultValue || '');
    setState({ title: 'Are you sure?', confirmText: 'Confirm', cancelText: 'Cancel', ...opts });
  }), []);

  const close = (result) => {
    resolver.current?.(result);
    resolver.current = null;
    setState(null);
  };

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <AlertDialog open={!!state} onOpenChange={(o) => !o && close(state?.withInput ? null : false)}>
        {state && (
          <AlertDialogContent className="max-w-sm" data-testid="confirm-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>{state.title}</AlertDialogTitle>
              {state.description && <AlertDialogDescription className="whitespace-pre-line">{state.description}</AlertDialogDescription>}
            </AlertDialogHeader>
            {state.withInput && (
              <div>
                {state.inputLabel && <Label className="text-xs">{state.inputLabel}</Label>}
                <Textarea rows={2} autoFocus value={value} onChange={(e) => setValue(e.target.value)} placeholder={state.placeholder || ''} className="mt-1" data-testid="confirm-dialog-input" />
              </div>
            )}
            <AlertDialogFooter>
              <Button variant="outline" onClick={() => close(state.withInput ? null : false)} data-testid="confirm-dialog-cancel">{state.cancelText}</Button>
              <Button
                className={state.destructive ? 'bg-rose-600 hover:bg-rose-700' : 'bg-teal-600 hover:bg-teal-700'}
                disabled={state.withInput && state.required && !value.trim()}
                onClick={() => close(state.withInput ? value.trim() : true)}
                data-testid="confirm-dialog-confirm"
              >
                {state.confirmText}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        )}
      </AlertDialog>
    </ConfirmContext.Provider>
  );
}

export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error('useConfirm must be used inside <ConfirmProvider>');
  return ctx;
}
